// src/features/tests/TestEditor.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, getDocs, updateDoc, query, where, collection } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './TestEditor.css';

const TestEditor = ({ testId, onClose, onTestUpdated }) => {
  const [test, setTest] = useState(null);
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedNewQuestions, setSelectedNewQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddQuestions, setShowAddQuestions] = useState(false);

  useEffect(() => {
    loadTestData();
  }, [testId]);

  const loadTestData = async () => {
    try {
      // Load test
      const testDoc = await getDoc(doc(db, 'tests', testId));
      if (!testDoc.exists()) {
        setError('Test not found');
        return;
      }

      const testData = { id: testDoc.id, ...testDoc.data() };
      setTest(testData);

      // Load current questions
      if (testData.selectedQuestionIds && testData.selectedQuestionIds.length > 0) {
        const questionsPromises = testData.selectedQuestionIds.map(id =>
          getDoc(doc(db, 'testQuestions', id))
        );
        
        const questionDocs = await Promise.all(questionsPromises);
        const questions = questionDocs
          .filter(doc => doc.exists())
          .map(doc => ({ id: doc.id, ...doc.data() }));
        
        setCurrentQuestions(questions);
      }

      // Load available questions for adding
      await loadAvailableQuestions(testData);

    } catch (err) {
      console.error('Error loading test:', err);
      setError('Failed to load test data');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableQuestions = async (testData) => {
    try {
      const testTypes = testData.testTypes || [];
      
      if (testTypes.length === 0) return;

      let allQuestions = [];

      for (const testType of testTypes) {
        const q = query(
          collection(db, 'testQuestions'),
          where('levelId', '==', testData.levelId),
          where('testType', '==', testType),
          where('lessonCode', '==', testData.oldLessonCode)
        );

        const snapshot = await getDocs(q);
        const questions = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        allQuestions = [...allQuestions, ...questions];
      }

      // Filter out questions already in the test
      const currentIds = testData.selectedQuestionIds || [];
      const available = allQuestions.filter(q => !currentIds.includes(q.id));

      setAvailableQuestions(available);
    } catch (err) {
      console.error('Error loading available questions:', err);
    }
  };

  const handleRemoveQuestion = (questionId) => {
    setCurrentQuestions(prev => prev.filter(q => q.id !== questionId));
  };

  const handleToggleNewQuestion = (questionId) => {
    setSelectedNewQuestions(prev => {
      if (prev.includes(questionId)) {
        return prev.filter(id => id !== questionId);
      } else {
        const totalQuestions = currentQuestions.length + prev.length + 1;
        if (totalQuestions > 15) {
          setError('Maximum 15 questions allowed');
          return prev;
        }
        return [...prev, questionId];
      }
    });
    setError('');
  };

  const handleAddSelectedQuestions = () => {
    const questionsToAdd = availableQuestions.filter(q => 
      selectedNewQuestions.includes(q.id)
    );
    
    setCurrentQuestions(prev => [...prev, ...questionsToAdd]);
    setAvailableQuestions(prev => 
      prev.filter(q => !selectedNewQuestions.includes(q.id))
    );
    setSelectedNewQuestions([]);
    setShowAddQuestions(false);
    setSuccess('Questions added! Remember to save changes.');
  };
    const [numQuestionsToUse, setNumQuestionsToUse] = useState(10);
  const handleSave = async () => {
    if (currentQuestions.length === 0) {
      setError('Test must have at least one question');
      return;
    }

    if (currentQuestions.length > 15) {
      setError('Test cannot have more than 15 questions');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updatedQuestionIds = currentQuestions.map(q => q.id);
      
      await updateDoc(doc(db, 'tests', testId), {
        selectedQuestionIds: updatedQuestionIds,
        'settings.numberOfQuestions': updatedQuestionIds.length,
        updatedAt: new Date().toISOString()
      });

      setSuccess('Test updated successfully!');
      
      if (onTestUpdated) {
        onTestUpdated();
      }

      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Error saving test:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getQuestionTypeLabel = (question) => {
    const typeLabels = {
      'mcq': 'MCQ',
      'multiple-response': 'Multiple Response',
      'type-in': 'Type In',
      'order': 'Order',
      'matching': 'Matching'
    };
    return typeLabels[question.questionType] || question.questionType;
  };

const getQuestionPreview = (question) => {
  const content = question.content || {};
  
  // Helper function to clean HTML and get text
  const cleanHTML = (html) => {
    if (!html) return '';
    // Remove all HTML tags and decode entities
    let text = html
      .replace(/<br\s*\/?>/gi, ' ')  // Replace <br> with space
      .replace(/<[^>]*>/g, '')        // Remove all HTML tags
      .replace(/&nbsp;/g, ' ')        // Replace &nbsp; with space
      .replace(/\s+/g, ' ')           // Collapse multiple spaces
      .trim();                        // Trim whitespace
    return text;
  };
  
  // MCQ - show stem
  if (content.stem) {
    const text = cleanHTML(content.stem);
    if (text) return text.substring(0, 100) + (text.length > 100 ? '...' : '');
  }
  
  // Type-in - show text
  if (content.text) {
    const text = cleanHTML(content.text);
    if (text) return text.substring(0, 100) + (text.length > 100 ? '...' : '');
  }
  
  // Order/Matching - show instructions
  if (content.instructions) {
    const text = cleanHTML(content.instructions);
    if (text) return text.substring(0, 100) + (text.length > 100 ? '...' : '');
  }
  
  // Order - show first item
  if (content.items && content.items.length > 0) {
    const firstItem = content.items[0].text || '';
    return `Order: ${firstItem.substring(0, 80)}${firstItem.length > 80 ? '...' : ''}`;
  }
  
  // Matching - show first pair
  if (content.pairs && content.pairs.length > 0) {
    return `Match: ${content.pairs[0].left} → ${content.pairs[0].right}`;
  }
  
  return '[No preview available]';
};

  if (loading) {
    return (
      <div className="test-editor-overlay">
        <div className="test-editor-modal">
          <div className="loading">Loading test data...</div>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="test-editor-overlay">
        <div className="test-editor-modal">
          <div className="error-message">Test not found</div>
          <button onClick={onClose} className="close-btn">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="test-editor-overlay">
      <div className="test-editor-modal">
        <div className="editor-header">
          <h2>Edit Test: {test.title}</h2>
          <button onClick={onClose} className="close-icon-btn">✕</button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="editor-content">
          {/* Current Questions Section */}
          <div className="current-questions-section">
            <div className="section-header">
              <h3>Current Questions ({currentQuestions.length}/15)</h3>
              {!showAddQuestions && availableQuestions.length > 0 && (
                <button 
                  onClick={() => setShowAddQuestions(true)}
                  className="add-questions-btn"
                  disabled={currentQuestions.length >= 15}
                >
                  + Add Questions
                </button>
              )}
            </div>

            <div className="questions-list">
              {currentQuestions.length === 0 ? (
                <div className="empty-message">No questions in this test</div>
              ) : (
                currentQuestions.map((question, index) => (
                  <div key={question.id} className="question-item">
                    <div className="question-number">{index + 1}</div>
                    <div className="question-info">
                      <div className="question-header">
                        <span className="question-type-badge">
                          {getQuestionTypeLabel(question)}
                        </span>
                        <span className="question-test-type">{question.testType}</span>
                      </div>
                      <div className="question-preview">
                        {getQuestionPreview(question)}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveQuestion(question.id)}
                      className="remove-btn"
                      title="Remove question"
                    >
                      ✕
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="test-settings-section">
                <h3>Test Settings</h3>
                <div className="form-group">
                    <label>Questions Per Test</label>
                    <input
                        type="number"
                        min="1"
                        max={Math.max(10, currentQuestions.length)} // At least 10
                        value={numQuestionsToUse}
                        onChange={(e) => setNumQuestionsToUse(Math.min(10, parseInt(e.target.value) || 10))}
                        disabled // Lock it to 10
                        />
                        <small>
                        Diagnostic tests always use 10 questions, randomly selected from your pool of {currentQuestions.length}.
                        </small>
                </div>
            </div>
          </div>

          {/* Add Questions Section */}
          {showAddQuestions && (
            <div className="add-questions-section">
              <div className="section-header">
                <h3>Add Questions ({availableQuestions.length} available)</h3>
                <button 
                  onClick={() => {
                    setShowAddQuestions(false);
                    setSelectedNewQuestions([]);
                  }}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>

              <div className="selection-info">
                Selected: {selectedNewQuestions.length} • 
                Will have: {currentQuestions.length + selectedNewQuestions.length}/15 total
              </div>

              <div className="questions-list">
                {availableQuestions.map(question => (
                  <div
                    key={question.id}
                    className={`question-item ${
                      selectedNewQuestions.includes(question.id) ? 'selected' : ''
                    }`}
                  >
                    <div className="question-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedNewQuestions.includes(question.id)}
                        onChange={() => handleToggleNewQuestion(question.id)}
                        disabled={
                          !selectedNewQuestions.includes(question.id) &&
                          currentQuestions.length + selectedNewQuestions.length >= 15
                        }
                      />
                    </div>
                    <div className="question-info">
                      <div className="question-header">
                        <span className="question-type-badge">
                          {getQuestionTypeLabel(question)}
                        </span>
                        <span className="question-test-type">{question.testType}</span>
                      </div>
                      <div className="question-preview">
                        {getQuestionPreview(question)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="add-actions">
                <button
                  onClick={handleAddSelectedQuestions}
                  className="confirm-add-btn"
                  disabled={selectedNewQuestions.length === 0}
                >
                  Add {selectedNewQuestions.length} Question(s)
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="editor-footer">
          <button onClick={onClose} className="cancel-footer-btn">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="save-btn"
            disabled={saving || currentQuestions.length === 0}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestEditor;